import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate } from '../api/dataverseClient';

// Server-side persisted card expansion state per (user, task). Schema
// verified via EntityDefinitions on Apr 19:
//   dagrun_pm_user_card_stateid (guid), dagrun_pm_name,
//   dagrun_pm_userid (string), dagrun_pm_taskid (string),
//   dagrun_pm_noteexpanded (bool), dagrun_pm_checklistexpanded (bool).

export interface CardState {
  dagrun_pm_user_card_stateid: string;
  dagrun_pm_userid?: string | null;
  dagrun_pm_taskid?: string | null;
  dagrun_pm_noteexpanded?: boolean | null;
  dagrun_pm_checklistexpanded?: boolean | null;
}

const FIELDS = [
  'dagrun_pm_user_card_stateid', 'dagrun_pm_userid', 'dagrun_pm_taskid',
  'dagrun_pm_noteexpanded', 'dagrun_pm_checklistexpanded',
];

// Fetch every card-state row for the given user in one query, keyed by
// taskId. Taking the whole user set is almost always smaller than a
// per-task round trip and lets the board resolve state for every visible
// card with zero network cost per card.
export async function getUserCardStates(pca: IPublicClientApplication, userId: string): Promise<Map<string, { noteExpanded: boolean; checklistExpanded: boolean; id: string }>> {
  try {
    const rows = await dvList<CardState>(pca, 'dagrun_pm_user_card_states', {
      select: FIELDS,
      filter: `dagrun_pm_userid eq '${userId}'`,
      top: 2000,
    });
    const map = new Map<string, { noteExpanded: boolean; checklistExpanded: boolean; id: string }>();
    rows.forEach(r => {
      if (!r.dagrun_pm_taskid) return;
      map.set(r.dagrun_pm_taskid, {
        id: r.dagrun_pm_user_card_stateid,
        noteExpanded: !!r.dagrun_pm_noteexpanded,
        checklistExpanded: !!r.dagrun_pm_checklistexpanded,
      });
    });
    return map;
  } catch {
    return new Map();
  }
}

// Upsert card state for (user, task). If an existing row is found, PATCH;
// otherwise POST a new row. Takes a cached existing row id when available
// to skip the lookup round trip on repeated toggles within a session.
export async function upsertCardState(
  pca: IPublicClientApplication,
  userId: string,
  taskId: string,
  noteExpanded: boolean,
  checklistExpanded: boolean,
  existingId?: string,
): Promise<string | null> {
  try {
    if (existingId) {
      await dvUpdate(pca, 'dagrun_pm_user_card_states', existingId, {
        dagrun_pm_noteexpanded: noteExpanded,
        dagrun_pm_checklistexpanded: checklistExpanded,
      });
      return existingId;
    }
    // Look up first — there might be a pre-existing row from another session.
    const rows = await dvList<CardState>(pca, 'dagrun_pm_user_card_states', {
      select: ['dagrun_pm_user_card_stateid'],
      filter: `dagrun_pm_userid eq '${userId}' and dagrun_pm_taskid eq '${taskId}'`,
      top: 1,
    });
    if (rows[0]) {
      await dvUpdate(pca, 'dagrun_pm_user_card_states', rows[0].dagrun_pm_user_card_stateid, {
        dagrun_pm_noteexpanded: noteExpanded,
        dagrun_pm_checklistexpanded: checklistExpanded,
      });
      return rows[0].dagrun_pm_user_card_stateid;
    }
    const newId = await dvCreate(pca, 'dagrun_pm_user_card_states', {
      dagrun_pm_name: `${userId}-${taskId}`,
      dagrun_pm_userid: userId,
      dagrun_pm_taskid: taskId,
      dagrun_pm_noteexpanded: noteExpanded,
      dagrun_pm_checklistexpanded: checklistExpanded,
    });
    return newId;
  } catch {
    return null;
  }
}
